import type { ClikArtPlacedIds } from './types/clikart-placed-ids'

/**
 * Reads the backend's placed-ClikArt response (`GET /clikart/placedIds` and
 * `POST /clikart/scanPlacedIds` share it): `{ placedClikArtIds: { [spreadId]: artId[] }, status,
 * lastScanTS }`. Returns null for anything that isn't that shape, which callers treat as a failed
 * load.
 *
 * `status` is `fresh`, `scanning` or `stale`; only `scanning` matters here, and any other or
 * missing value counts as "not scanning". `lastScanTS` is a zero time (year 1) while a book has
 * never been scanned, so a parsed value above 0 is what says a scan has finished.
 */
export function normalizeClikArtPlacedIds(response: unknown): ClikArtPlacedIds | null {
  if (response === null || typeof response !== 'object') return null

  const { placedClikArtIds, status, lastScanTS } = response as {
    placedClikArtIds?: unknown
    status?: unknown
    lastScanTS?: unknown
  }

  if (placedClikArtIds === null || typeof placedClikArtIds !== 'object') return null
  if (Array.isArray(placedClikArtIds)) return null

  const placedBySpread: Record<string, string[]> = {}

  for (const [spreadId, artIds] of Object.entries(placedClikArtIds)) {
    if (!Array.isArray(artIds)) return null

    const ids = artIds.filter((artId): artId is string => typeof artId === 'string' && artId !== '')

    if (ids.length > 0) placedBySpread[spreadId] = ids
  }

  const scannedAt = typeof lastScanTS === 'string' ? Date.parse(lastScanTS) : Number.NaN

  return {
    placedBySpread,
    isScanning: status === 'scanning',
    hasScanned: Number.isFinite(scannedAt) && scannedAt > 0,
  }
}
